import type { DiscoveryResult } from '@nx/constants';
import { fetchDiscoveryData } from '@nx/state';
import * as StdLib from '@nx/stdlib';
import * as React from 'react';

type FetchDiscoveryDataArgs = Parameters<typeof fetchDiscoveryData>;
type FetchDiscoveryUrl = FetchDiscoveryDataArgs[0];
type FetchDiscoveryOptions = FetchDiscoveryDataArgs[1] & { debounce?: number };

async function debouncedFetchDiscoveryData(url: FetchDiscoveryUrl, { debounce, ...options }: FetchDiscoveryOptions) {
  if (debounce !== undefined && debounce > 0) {
    await new Promise((resolve) => setTimeout(resolve, debounce));
  }
  return fetchDiscoveryData(url, options);
}

type DiscoveryState = { status: string; url: string | null; data?: DiscoveryResult } & (
  | {
      status: 'idle';
      url: null;
    }
  | {
      data?: DiscoveryResult;
      status: 'pending';
      url: string;
    }
  | {
      data: DiscoveryResult;
      status: 'resolved';
      url: string;
    }
  | {
      error: Error;
      status: 'rejected';
      url: string;
    }
);

type DiscoveryAction =
  | {
      type: 'fetch';
      url: string;
    }
  | {
      type: 'resolved';
      data: DiscoveryResult;
    }
  | {
      type: 'rejected';
      error: Error;
    };

function reducer(state: DiscoveryState, action: DiscoveryAction): DiscoveryState {
  switch (action.type) {
    case 'fetch': {
      return {
        ...state,
        status: 'pending',
        url: action.url,
      };
    }

    case 'rejected': {
      if (state.status !== 'pending') {
        return state;
      }

      return {
        error: action.error,
        status: 'rejected',
        url: state.url,
      };
    }

    case 'resolved': {
      if (state.status !== 'pending') {
        return state;
      }

      return {
        data: action.data,
        status: 'resolved',
        url: state.url,
      };
    }

    default: {
      return state;
    }
  }
}

function useDiscoveryState() {
  const [state, dispatch] = React.useReducer(reducer, { status: 'idle', url: null });

  const fetch = React.useCallback(
    (url: string) =>
      dispatch({
        type: 'fetch',
        url,
      }),
    [dispatch],
  );

  const resolve = React.useCallback(
    (data: DiscoveryResult) =>
      dispatch({
        type: 'resolved',
        data,
      }),
    [dispatch],
  );

  const reject = React.useCallback(
    (error: Error) =>
      dispatch({
        type: 'rejected',
        error,
      }),
    [dispatch],
  );

  return {
    fetch,
    reject,
    resolve,
    state,
  };
}

export function useNeo4jDiscovery(url: string, options: { debounce?: number }) {
  const { fetch, reject, resolve, state } = useDiscoveryState();

  React.useEffect(() => {
    if (!StdLib.URLs.Neo4jURL.isValid(url)) {
      return () => {
        // no-op
      };
    }

    const abortController = new AbortController();
    const neo4jUrl = StdLib.URLs.Neo4jURL.asNullable(url);

    if (neo4jUrl !== null) {
      const discoveryUrl = neo4jUrl.toDiscoveryUrl();
      fetch(discoveryUrl);
      void debouncedFetchDiscoveryData(discoveryUrl, {
        debounce: options.debounce ?? 500,
        signal: abortController.signal,
      })
        .then((result) => {
          if (!abortController.signal.aborted) {
            if (result.status === 'FetchError') {
              throw new Error(result.message);
            }
            resolve(result);
          }
        })
        .catch((error) => {
          if (!abortController.signal.aborted) {
            if (error instanceof Error) {
              reject(error);
            } else {
              reject(new Error('Unknown error'));
            }
          }
        });
    }

    return () => {
      abortController.abort();
    };
  }, [fetch, options.debounce, resolve, reject, url]);

  return state;
}
